import PanelModelEnum from "../enums/PanelModeEnum";
import AlarmCommandEnum from "../enums/AlarmCommandEnum";
import Alarm from "../models/Alarm";
import Event from "../models/Event";
import PlayedMediaControl from "../models/PlayedMediaControl";
import Session from "../models/Session";
import EventCommandEnum from "../enums/EventCommandEnum";
import GuardiaoStatusEnum from '../enums/GuardiaoStatusEnum';
import { alertTitleClasses } from "@mui/material";
import User from "../models/User";

class CommandsProvider {
	constructor() {
		this.currentAlarm = null; // Alarme atual
		this.lastAlarm = null; // Último alarme registrado
		this.panelMode = PanelModelEnum.NAO_INFORMADO; // Modo de Operação da Central
		this.guardiaoStatus = GuardiaoStatusEnum.NAO_INFORMADO;
		this.modemStatus = false; // Status do Modem
		this.ACDCStatus = true; // Tem Rede ACDC?
		this.showLowBattery = true; // Apresenta Eventos de Bateria Baixa dos equipamentos
		this.clearBackdrop = null; // Controle de fechamento do AlarmBackdrop
		this.groupAlarms = []; // Lista de alarmes por grupo
		this.groupEvents = []; // Lista de eventos por grupo
		this.subscribers = []; // Lista de funções para notificar atualizações
		this.playedMediaControl = []; // Lista de Ocorrências tocadas ou não
		this.session = this.getSession() || {}; //Busca dados da sessão
		this.user = this.getUser() || {};

		// Controle do pulso visual (gerenciado centralmente)
		this.shouldPulseEvents = false;
		this.shouldPulseAlarms = false;

		// Cache para detectar mudanças reais de pulso (evita logs excessivos)
		this._lastPulseEvents = undefined;
		this._lastPulseAlarms = undefined;

		this.checkLicenseStatus(); // Inicia verificação de licença
		this.checkDesktopStatusFromWeb();
		this.updateVersionInfo = {
			canUpdate: false,
			version: "",
			lastCheckDate: null
		};
		this.checkUpdateStatus();// Inicia verificação de updates
	}

	getUser() {
		return User.fromSession(this.session)
	}

	// Atualiza dados da sessão 
	getSession() {
		const userJson = localStorage.getItem('user');
    	if (userJson)
		{
			let session = userJson ? JSON.parse(userJson) : null;
			let existingSession = this.session; // Preserva a sessão atual

			if (session.dS_PANEL_SERIAL === undefined || session.dS_PANEL_SERIAL === null) {
				session.dS_PANEL_SERIAL = JSON.parse(localStorage.getItem('system')) != null 
					? JSON.parse(localStorage.getItem('system')).serialKeys 
					: null;
			}

			if(session.config === undefined || session.config === null) {
				session.config = {};
			}

			// Cria nova sessão preservando dados de licença se existirem
			let newSession = new Session(
				session.id, 
				session.iD_EMPRESA, 
				session.iD_INTRANET, 
				session.booL_MASTER, 
				session.dS_NOME, 
				session.email, 
				session.senha, 
				session.dS_CNPJ, 
				session.dS_PANEL_SERIAL, 
				session.role || session.roles, 
				session.config
			);

			// Se existir uma sessão anterior, preserva os dados de licença
			if (existingSession) {
				newSession.setLicenseInfo(
					existingSession.getLicenseType(),
					existingSession.getLicenseValidDays(),
					existingSession.isLicenseValid(),
					existingSession.getLicenseElements()
				);
			}

			return newSession;
		}
		else
		{
			return new Session();
		}
	}

	setSession(session) {
		if (!session) {
			console.warn("Tentativa de setSession com session nula!");
			return;
		}

		this.session = session;
		
		let controlSession = JSON.parse(localStorage.getItem("user"));

		// Checagem de segurança para roles e license
		controlSession.roles = session.roles || session.role || {};
		controlSession.license = session.license || {};
		controlSession.config = session.config || {};
		controlSession.DS_PANEL_SERIAL = session.serial;
		controlSession.serial = session.serial;

		localStorage.setItem('user', JSON.stringify(controlSession)); // Persistir no localStorage
		
		this.notifySubscribers();
	}

	// Retorna dados necessários para as requisições
	getSettings()
	{
		return {
			companyId: JSON.parse(localStorage.getItem('user')).iD_EMPRESA,
			token: localStorage.getItem("token"),
		}
	}

	getPanelMode()
	{
		return this.panelMode;
	}
	
	getACDCStatus()
	{
		return !(this.groupEvents.some((existingEvent) => existingEvent.command === EventCommandEnum.SEM_REDE_AC));
	}
	
	getShowLowBattery()
	{
		return this.showLowBattery;
	}

	setShowLowBattery(status)
	{
		this.showLowBattery = status;
		this.notifySubscribers();
	}

	getCurrentAlarm() {
		return this.currentAlarm;
	}

	getLastAlarm() {
		const filteredAlarms = this.groupAlarms
			.filter(alarm => alarm.groupValue.charAt(12) === '0');

		if (filteredAlarms.length === 0) return null;		

		return filteredAlarms
			.sort((a, b) => new Date(b.datetime) - new Date(a.datetime))[0];
	}
  
	// Atualiza o alarme atual e mantém o último
	updateCurrentAlarmState(alarm) {
		if (!(alarm instanceof Alarm)) 
		{
			throw new Error("O objeto fornecido não é uma instância válida de Alarm.");
		}
		if (this.currentAlarm) 
		{
			this.lastAlarm = { ...this.currentAlarm };
		}
	
		this.playedMediaControl.push(new PlayedMediaControl(alarm.id));
		this.currentAlarm = alarm;
		this.notifySubscribers();
	}
  
	// Converte uma mensagem recebida em um alarme
	convertMessageToAlarm(message) {
		try
		{
			let settings = this.getSettings();

			return new Alarm(
				message.id,
				settings.companyId,
				message.fK_GRUPO,
				message.dS_LOCAL_ALARME,
				message.dS_DESCRICAO_ALARME,
				message.dS_ORIGEM_ALARME,
				this.parseDate(message.dT_EVENTO) ?? this.parseDate(message.dT_ALARME) // dT_EVENTO se definido, senão dT_ALARME
			);
		}
		catch (error)
		{
			console.log("🚀 ~ CommandsProvider ~ convertMessageToAlarm ~ error", error.message)
		}
	}

	// Adiciona eventos na lista de GroupEvents
	updateGroupEventsState(event)
	{
		if (!(event instanceof Event)) {
			throw new Error("O objeto fornecido não é uma instância válida de Event.");
		}

		this.groupEvents = this.groupEvents.filter(
			(existingEvent) => existingEvent.id !== event.id
		);

		this.playedMediaControl.push(new PlayedMediaControl(event.id));
		this.groupEvents.push(event);
		this.updatePulseState(); // Atualiza o pulso
		this.notifySubscribers();
	}

	// Converte uma mensagem recebida em um evento
	convertMessageToEvent(message) {
		try
		{
			let settings = this.getSettings();

			if (message.type === "alarme")
			{
				return new Alarm(message.id,settings.companyId, message.fK_GRUPO, message.dS_LOCAL_EVENTO, message.fK_OCORRENCIA, message.dS_ORIGEM_ALARME, this.parseDate(message.dT_EVENTO) ?? this.parseDate(message.dT_ALARME));
			}
			else
			{
				return new Event(message.id,message.fK_OCORRENCIA, message.dS_DESCRICAO_EVENTO, message.dS_ID_PERIFERICO, message.dS_LOCAL_EVENTO, message.dS_POS_MEM, message.fK_CENTRAL, message.fK_GRUPO, this.parseDate(message.dT_EVENTO), message.dS_ORIGEM_ALARME);
			}
		}
		catch (error)
		{
			console.log("🚀 ~ CommandsProvider ~ convertMessageToEvent ~ error:", error.message)
		}
	}

	parseDate(dateString) {

		// Verifica se a string da data está no formato ISO 8601
		const iso8601Regex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{1,7}([+-]\d{2}:\d{2})?$/;

		if (iso8601Regex.test(dateString)) {
			const date = new Date(dateString);

			const day = String(date.getDate()).padStart(2, '0');
			const month = String(date.getMonth()).padStart(2, '0');
			const year = date.getFullYear();
			const hours = String(date.getHours()).padStart(2, '0');
			const minutes = String(date.getMinutes()).padStart(2, '0');
			const seconds = String(date.getSeconds()).padStart(2, '0');

			return new Date(year, month, day, hours, minutes, seconds);

			//return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;

		} else {
			return 'Invalid Date';
		}
	}

	// Atualiza o estado da mídia tocada
	updatePlayedMedia(playedMedia)
	{
		if (!(playedMedia instanceof PlayedMediaControl)) 
		{
			throw new Error("O objeto fornecido não é uma instância válida de PlayedMediaControl.");
		}

		// Encontre o índice do item correspondente no array
		const index = this.playedMediaControl.findIndex(item => item.id === playedMedia.id);

		if (index !== -1) {
			// Atualize o item no array
			this.playedMediaControl[index] = playedMedia;
		} else {
			// Caso o item não exista no array, você pode optar por adicioná-lo (opcional)
			this.playedMediaControl.push(playedMedia);
		}

		// Notifique os inscritos sobre a mudança
		this.notifySubscribers();
	}

	removeResetedGroups() {
		setTimeout(() => {
			// Encontra o grupo que deve ser restaurado
			const resetedGroup = this.groupAlarms.find(
				(ga) => ga.command === AlarmCommandEnum.RESTAURAR
			);
	
			if (!resetedGroup) {
				console.warn("Nenhum grupo com RESTAURAR encontrado");
				return;
			}
	
			// Remove os Events vinculados ao grupo
			this.groupEvents = this.groupEvents.filter(
				(existingEvent) => existingEvent.position !== resetedGroup.groupValue
			);
	
			// Remove os Alarms vinculados ao grupo
			this.groupAlarms = this.groupAlarms.filter(
				(existingAlarm) => existingAlarm.groupValue !== resetedGroup.groupValue
			);

			// Atualiza ClearBackdrop quando não há mais conteúdo nele
			if (this.groupAlarms.length === 0 && this.groupEvents.length === 0)
			{
				this.clearBackdrop = Math.random();
			}

			// Atualiza o pulso
			this.updatePulseState();

			// Notifica os inscritos
			this.notifySubscribers();
		}, 3000);
	}

	// Processa um alarme gerado
	processAlarm(alarm) {
		if (!(alarm instanceof Alarm)) {
			throw new Error("O objeto fornecido não é uma instância válida de Alarm.");
		}

		let _isResetForSpecificGroup = false;

		this.groupAlarms.push(alarm);
		this.updatePulseState(); // Atualiza o pulso

		if (alarm.isDeactivateCommand()) 
		{
			if (alarm.isGeneral())
			{
				setTimeout(() => {

					if (this.groupEvents.some((existingEvent) => existingEvent.command === EventCommandEnum.SEM_REDE_AC))
					{
						//Pegar o último evento que será SEM_REDE_AC
						const lastReset = this.currentAlarm && this.currentAlarm.command === AlarmCommandEnum.RESTAURAR && this.currentAlarm;
						console.log("🚀 ~ CommandsProvider ~ setTimeout ~ lastReset:", lastReset)

						if (lastReset) 
						{
							console.log('Último Reset:', lastReset.datetime);
							this.groupEvents = this.groupEvents.filter((event) => {
								console.log('Comparando:', event.datetime, lastReset.datetime);
								return event.datetime > lastReset.datetime;
							});
						}
						
						//Limpar playedMediaControl de acordo com o id do último evento. O que não for, será deletado.
						this.groupAlarms = [];
						this.currentAlarm = null;

						if (this.groupEvents.length === 0)
						{
							this.playedMediaControl = [];
							this.clearBackdrop = Math.random();
						}

						this.updatePulseState(); // Atualiza o pulso
					}
					else
					{
						this.groupAlarms = [];
						this.currentAlarm = null;
						this.playedMediaControl = [];
						this.groupEvents = [];
						this.clearBackdrop = Math.random();
						this.updatePulseState(); // Atualiza o pulso
					}

					this.notifySubscribers();
				}, 3000);
			}
			else 
			{
				_isResetForSpecificGroup = true;
			}
		}
	
		this.notifySubscribers();

		if (_isResetForSpecificGroup)
		{
			this.removeResetedGroups();
		}
	}

	// Envia o comando para o sistema
	async sendAlarm(command, group) {
		const settings = this.getSettings();

		const alarm = new Alarm(null, settings.companyId, group.position, group.local || group.position.slice(12), command, "guardiao", null);
	
		try 
		{
			const request = await fetch(`${process.env.REACT_APP_URL}/command/send`, {
				method: 'POST',
				headers: {
					"content-type": "application/json",
					"Authorization": `Bearer ${settings.token}`,
				},
				body: JSON.stringify(alarm),
			});
		
			if (!request.ok) throw new Error('Erro ao enviar comando');
			const response = await request.json();
			this.checkAlarm(response);
	
		}
		catch (error) 
		{
		  	console.error('🚀 ~ CommandsProvider ~ sendAlarm ~ error', error.message);
		}
	}
	
	checkAlarm(response) {
		console.log('Alarme recebido:', response);
		if (response?.id && response?.dS_DESCRICAO_ALARME) 
		{
			let settings = this.getSettings();
			let alarm = new Alarm(response.id, settings.companyId, response.fK_GRUPO, response.dS_LOCAL_ALARME, response.dS_DESCRICAO_ALARME, response.dS_ORIGEM_ALARME, response.dT_EVENTO ? this.parseDate(response.dT_EVENTO) : this.parseDate(response.dT_ALARME));
			this.updateCurrentAlarmState(alarm);
			this.processAlarm(alarm)
		}
		else
		{
			throw new Error("O alarme não foi registrado");
		}
	}

	updateGuardianStatus(status) {
        // Se o status mudou para OFFLINE, mantém
        if (status === GuardiaoStatusEnum.OFFLINE) {
            this.guardiaoStatus = GuardiaoStatusEnum.OFFLINE;
            this.notifySubscribers();
            return;
        }

        // Se o status mudou para ONLINE, verifica modem
        if (status === GuardiaoStatusEnum.ONLINE) {
            this.guardiaoStatus = this.modemStatus ? 
                GuardiaoStatusEnum.ONLINE : 
                GuardiaoStatusEnum.ONLINE_SEM_MODEM;
        }

        this.notifySubscribers();
    }

	updateModemStatus(status) {
        console.log("🚀 ~ CommandsProvider ~ updateModemStatus ~ status:", status)
        this.modemStatus = status;

        this.notifySubscribers();
    }
  
	updatePanelMode(message)
	{
		this.panelMode = message; //TODO - Aplicar ENUM para o Modo de Operação da Central
		this.notifySubscribers();
	}

	async checkDesktopStatusFromWeb() {
        if (process.env.REACT_APP_MODE !== "web") {
            console.log("Ambiente Desktop identificado, pulando checkDesktopStatusFromWeb");
            return;
        }

        try {
            if (!localStorage.getItem("token") || !sessionStorage.getItem("serial")) {
                throw new Error("Credenciais não encontradas");
            }

			const serial = sessionStorage.getItem("serial");

            const response = await fetch(
                `${process.env.REACT_APP_URL}/localstatus/get-by-serial?serialNumber=${serial}`,
                {
                    method: "GET",
                    headers: {
                        "content-type": "application/json",
                        "Authorization": "Bearer " + localStorage.getItem("token")
                    }
                }
            );

            if (!response.ok) {
                throw new Error("Falha ao buscar dados do Modo de Operação");
            }

            const res = await response.json();
            
            // Atualiza o status do guardião baseado na resposta
            this.updateGuardianStatus(res.isOpen ? 
                GuardiaoStatusEnum.ONLINE : 
                GuardiaoStatusEnum.OFFLINE
            );

        } catch (error) {
            console.log("🚀 ~ CommandsProvider ~ checkDesktopStatusFromWeb ~ error:", error);
            this.updateGuardianStatus(GuardiaoStatusEnum.OFFLINE);
        } finally {
            // Agenda próxima verificação
            setTimeout(() => this.checkDesktopStatusFromWeb(), 10000);
        }
    }

	// Verifica o status da licença
	async checkLicenseStatus() {
		console.log("🚀 ~ CommandsProvider ~ checkLicenseStatus ~ START");
		let now = false;

		try {
			const serial = sessionStorage.getItem("serial");
			if (serial) {
				const settings = this.getSettings();
				const response = await fetch(`${process.env.REACT_APP_URL}/central/licence?serialNumber=${serial}`, {
					method: 'GET',
					headers: {
						"content-type": "application/json",
						"authorization": `Bearer ${settings.token}`
					}
				});

				if (!response.ok) {
					throw new Error("Não foi possível buscar dados de Licenças");
				}

				const res = await response.json();
				console.log("🚀 ~ CommandsProvider ~ checkLicenseStatus ~ res:", res);

				if (res.licenceElements != null) {
					this.checkLicenseElements(res.licenceElements);
				} else {
					this.checkLicenseElements(['-']);
				}
			} else {
				now = true;
				return; // Serial ainda não disponível, retry agendado no finally
			}
		} catch (error) {
			console.log("🚀 ~ CommandsProvider ~ checkLicenseStatus ~ error:", error);
		} finally {
			// Agenda próxima verificação
			setTimeout(() => this.checkLicenseStatus(), now ? 2000 : 300000); // 2s ou 5min
			//setTimeout(() => this.checkLicenseStatus(), now ? 2000 : 43200000); // 1 dia
		}
	}

	forceCheckLicenseStatus() {
		this.checkLicenseStatus();
	}

	async getLicenseHddTest(serial) {
		try {
			const request = await fetch(`${process.env.REACT_APP_URL}/central/technician/hdd-serials?serialNumber=${serial}`, {
				method: "GET",
				headers: { "content-type": "application/json" }
			});

			if (!request.ok) {
				throw new Error("Não foi possível buscar dados de Licença");
			}

			const res = await request.json();
			return res;
		}
		catch (error) {
			console.log("🚀 ~ CommandsProvider ~ getLicenseHddTest ~ error:", error.message);
		}
	}

	async verifyFreeLicenseatWeb(serial) {
		try {
			const request = await fetch(`${process.env.REACT_APP_URL}/licences/free/verify?serialNumber=${serial}`, {
				method: "POST",
				headers: { "content-type": "application/json" }
			});

			if (!request.ok) {
				throw new Error("Não foi possível buscar dados de Licença free");
			}
			else
			{
                console.log("🚀 ~ CommandsProvider ~ verifyFreeLicenseatWeb ~ Licença free verificada com sucesso");
			}
			
		}
		catch (error) {
			console.log("🚀 ~ CommandsProvider ~ verifyFreeLicenseatWeb ~ error:", error.message);
		}
	}

	// Atualiza informações de licença na sessão
	checkLicenseElements(licenceElements) {
		let session = this.getSession();
		const licenseType = this.getLicenseName(licenceElements[0]);
		
		if (licenceElements.length >= 3) 
		{
			const startdate = new Date();
			const [day, month, year] = licenceElements[2].split("-");
			const endDate = new Date(year, month - 1, day);
			const difInDays = Math.ceil((endDate - startdate) / (1000 * 60 * 60 * 24));
			
			if (licenceElements[0] === "t") {
				// Para licença tipo técnico, fazemos a validação em duas etapas
				this.getLicenseHddTest(sessionStorage.getItem("serial"))
					.then(hddTest => {
						const isValid = hddTest ? 
							(hddTest.savedHddSerial === hddTest.currentHddSerial && difInDays >= 0) : 
							false;
						
						this.updateLicenseInfo(session, licenseType, difInDays, isValid, licenceElements);
					});
			} else {
				// Para outros tipos de licença, validamos apenas a data
				const isValid = difInDays >= 0;
				this.updateLicenseInfo(session, licenseType, difInDays, isValid, licenceElements);
			}
		}
		else 
		{
			this.updateLicenseInfo(session, licenseType, null, false, null);
		}
	}

	// Atualiza informações de licença na sessão
	updateLicenseInfo(session, licenseType, difInDays, isValid, licenceElements) {
		// Atualiza a sessão com as informações de licença
		session.setLicenseInfo(
			licenseType,
			difInDays,
			isValid,
			licenceElements
		);
		
		// Atualiza o role.light baseado no tipo de licença
		if (licenseType === "Free" || licenseType === "Inválida") {
			session.roles.light = true;
		} 
		else {
			session.roles.light = false;
			session.roles.tecnico = licenseType === "Técnico" ? true : false;
			session.roles.revenda = licenseType === "Integrador" ? true : false;
		}
		
		// Persiste as informações
		this.setSession(session);
		localStorage.setItem("licenseType", licenseType);
		console.log("🚀 ~ CommandsProvider ~ updateLicenseInfo ~ session:", this.session);
	}

	// Retorna o nome da licença baseado no tipo
	getLicenseName(licenceType) {
		console.log("🚀 ~ CommandsProvider ~ getLicenseName ~ licenceType:", licenceType)
		switch(licenceType) {
			case "d": return "Pro Local";
			case "f": return "Free";
			case "t": return "Técnico";
			case "r": return "Integrador";
			case "w": return "Pro Web";
			case "m": return "Master";
			default: return "Inválida";
		}
	}

	async checkUpdateStatus() {
		let retryDelay = 600000; // 10 minutos (execução normal)
		try {
			// Se não temos uma sessão válida ainda, agenda retry rápido e sai sem logar erro
			if (!this.session?.id || !this.session?.license?.type) {
				retryDelay = 2000; // retry em 2s até a sessão estar disponível
				return;
			}

			const lastCheck = localStorage.getItem('lastCheckDate');
			const today = new Date().toISOString();

			if (lastCheck !== today) {
				await this.verifyUpdate();
				localStorage.setItem('lastCheckDate', today);
			}

		} catch (error) {
			console.error("Erro ao verificar updates:", error);
		}
		finally {
			//setTimeout(() => this.checkUpdateStatus(), 10000); // 10 segundos
			setTimeout(() => this.checkUpdateStatus(), retryDelay); // 2s (sessão não pronta) ou 10 minutos (normal)
			//setTimeout(() => this.checkUpdateStatus(), 43200000); //1 dia
		}
	}

    async verifyUpdate() {
		try {
			// Usa dados da sessão ao invés do localStorage
			const userId = this.session.id;
			const licenseType = this.session.license.type;
			const licenseKey = this.session.license.elements?.[1];
			const token = localStorage.getItem("token");

			if (!userId || !licenseType || !licenseKey || !token) {
				throw new Error("Sessão não possui todos os dados necessários");
			}

			const updateResponse = await fetch(`${process.env.REACT_APP_API_URL}/licences/verify-version-to-update`, {
				method: 'POST',
				headers: {
					"Content-type": "application/json",
					"Authorization": `Bearer ${token}`
				},
				body: JSON.stringify({
					licenseKey: licenseKey,
					licenseType: licenseType,
					licenseRevision: process.env.REACT_APP_BUILD_VERSION
				})
			});

			if (!updateResponse.ok) throw new Error("Falha ao verificar updates");
			const updateResult = await updateResponse.json();

			// Verifica se o usuário pode receber notificação
			const notificationResponse = await fetch(`${process.env.REACT_APP_URL}/usernotification/get/${userId}`, {
				method: "GET",
				headers: { "Content-Type": "application/json" }
			});

			if (!notificationResponse.ok) throw new Error("Falha ao verificar notificações");
			const notificationResult = await notificationResponse.json();

			this.updateVersionInfo = {
				canUpdate: updateResult.update && notificationResult.booL_ISNOTIFIABLE,
				version: updateResult.version,
				lastCheckDate: new Date().toISOString()
			};

			this.notifySubscribers();
		} catch (error) {
			console.error("Erro em verifyUpdate:", error);
			throw error;
		}
	}

	// Adiciona um subscriber
	subscribe(callback) {
	  	this.subscribers.push(callback);
	}
  
	// Remove um subscriber
	unsubscribe(callback) {
	  	this.subscribers = this.subscribers.filter((cb) => cb !== callback);
	}
  
	// Atualiza o estado do pulso baseado nos arrays atuais
	updatePulseState() {
		// Verifica se há eventos reais (não DESL/RESTAURAR)
		const hasRealEvents = this.groupEvents.some(ev => 
			ev.command !== EventCommandEnum.SISTEMA_RESTAURADO &&
			ev.description !== "DESL" &&
			ev.type !== "DESL"
		);

		// Verifica se há alarmes reais (não DESL/RESTAURAR)
		const hasRealAlarms = this.groupAlarms.some(alarm => 
			alarm.command !== "DESL" &&
			alarm.command !== "SISTEMA RESTAURADO"
		);

		const previousEventState = this.shouldPulseEvents;
		const previousAlarmState = this.shouldPulseAlarms;

		this.shouldPulseEvents = hasRealEvents;
		this.shouldPulseAlarms = hasRealAlarms;

		// Log apenas se houver mudança
		if (previousEventState !== this.shouldPulseEvents) {
			console.log(`✨ Pulso Eventos: ${previousEventState} → ${this.shouldPulseEvents}`);
		}
		if (previousAlarmState !== this.shouldPulseAlarms) {
			console.log(`✨ Pulso Alarmes: ${previousAlarmState} → ${this.shouldPulseAlarms}`);
		}
	}

	// Notifica todos os inscritos
	notifySubscribers() {
		const caller = new Error().stack.split('\n')[2]?.trim() || 'unknown';

		// 🔍 DEBUG: Mostra apenas mudanças reais no estado de pulso
		const currentState = this.getState();
		const pulseChanged = 
			currentState.shouldPulseEvents !== this._lastPulseEvents ||
			currentState.shouldPulseAlarms !== this._lastPulseAlarms;

		if (pulseChanged || !this._lastPulseEvents) {
			console.log(`🔔 notifySubscribers (pulso mudou):`, {
				caller: caller.substring(0, 100),
				shouldPulseEvents: currentState.shouldPulseEvents,
				shouldPulseAlarms: currentState.shouldPulseAlarms,
				eventsCount: currentState.groupEvents.length,
				alarmsCount: currentState.groupAlarms.length
			});
			this._lastPulseEvents = currentState.shouldPulseEvents;
			this._lastPulseAlarms = currentState.shouldPulseAlarms;
		}

		this.subscribers.forEach((callback) => callback(currentState));
	}
  
	// Retorna o estado atual
	getState() {
		return {
			currentAlarm: this.currentAlarm,
			lastAlarm: this.lastAlarm,
			groupAlarms: this.groupAlarms,
			groupEvents: this.groupEvents,
			panelMode: this.panelMode,
			guardiaoStatus: this.guardiaoStatus,
			modemStatus: this.modemStatus,
			ACDCStatus : this.getACDCStatus(),
			showLowBattery: this.showLowBattery,
			clearBackdrop: this.clearBackdrop,
			playedMediaControl: this.playedMediaControl,
			session: this.session,
			updateVersionInfo: this.updateVersionInfo,
			shouldPulseEvents: this.shouldPulseEvents,
			shouldPulseAlarms: this.shouldPulseAlarms
		};
	}
}
  
const commandsProvider = new CommandsProvider();

// Registra no window para debug
if (process.env.NODE_ENV === 'development') {
    window.commandsProvider = commandsProvider;
}

export default commandsProvider;